import { Html5Qrcode } from "html5-qrcode";
import { useEffect, useRef, useState, useCallback } from "react";
import { getUserByBarcode, submitUserForm } from "../../API/index.ts";
import { calculateInspectionScore } from "../../Utils/scorelogic.ts";

const VolunteerPanel = ({ currentUser }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [scanner, setScanner] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [error, setError] = useState(null);

const [formData, setFormData] = useState({
  isfoodBusiness: "",
  useOfPlastic: "",
  biodegradableMaterialsUsed: "",
  dustbinsAvailable: "",
  wasteDisposedProperly: "",
  cleanlinessRating: "",
  wasteOnGround: "",
  remarks: "",
  photos: [],
});


  const qrRef = useRef(null);
  const scannerRunning = useRef(false);

  // ✅ Initialize Scanner
useEffect(() => {
  if (isScanning && qrRef.current) {
    const html5QrCode = new Html5Qrcode("qr-reader");
    setScanner(html5QrCode);

    const qrCodeSuccessCallback = async (decodedText) => {
      if (scannerRunning.current) {
        await html5QrCode.stop().catch(() => {});
        scannerRunning.current = false;
        setIsScanning(false);

        console.log("✅ QR Decoded:", decodedText);

        const match = decodedText.match(/ID:([A-Za-z0-9]+)/);
        const extractedId = match ? match[1] : null;

        if (!extractedId) {
          alert("Invalid QR format. Please scan a valid barcode.");
          return;
        }

        try {
          const userData = await getUserByBarcode(extractedId);
          console.log("✅ User Data from API:", userData);
          setSelectedUser(userData); // 👉 Show form now
        } catch (err) {
          console.error("❌ Fetch error:", err);
          alert("Failed to fetch user info. Try again." + (err?.detail || ""));
        }
      }
    };

    Html5Qrcode.getCameras()
      .then((devices) => {
        if (devices && devices.length) {
          console.log("📷 Available cameras:", devices);

          // ✅ Prefer back camera if found
          const backCamera = devices.find((device) =>
            device.label.toLowerCase().includes("back")
          );

          const cameraId = backCamera ? backCamera.id : devices[devices.length - 1].id;

          html5QrCode
            .start(
              cameraId,
              { fps: 10, qrbox: { width: 250, height: 250 } },
              qrCodeSuccessCallback,
              (err) => console.debug("Scanning...", err)
            )
            .then(() => {
              scannerRunning.current = true;
              console.log("✅ Camera started:", backCamera ? "Back camera" : "Default camera");
            })
            .catch((err) => {
              console.error("Camera start failed:", err);
              setError("Camera access failed. Please refresh.");
              setIsScanning(false);
            });
        } else {
          setError("No camera found.");
          setIsScanning(false);
        }
      })
      .catch((err) => {
        console.error("Camera error:", err);
        setError("Camera not accessible.");
        setIsScanning(false);
      });

    // Cleanup
    return () => {
      if (scannerRunning.current) {
        html5QrCode.stop().catch(() => {});
        scannerRunning.current = false;
      }
    };
  }
}, [isScanning]);


  // ✅ Actions
  const handleStartScan = () => {
    setError(null);
    setSelectedUser(null);
    setFormData({
      isfoodBusiness: "",
      useOfPlastic: "",
      biodegradableMaterialsUsed: "",
      dustbinsAvailable: "",
      wasteDisposedProperly: "",
      cleanlinessRating: "",
      wasteOnGround: "",
      sanitationRating: "",
      remarks: "",
      photos: [],
    });
    setIsScanning(true);
  };

  const handleFormChange = (e) => {
    console.log("Form change:", e.target);
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (e, index) => {
    const file = e.target.files[0];
    if (!file) return;
    setFormData((prev) => {
      const updatedPhotos = [...prev.photos];
      updatedPhotos[index] = file;
      return { ...prev, photos: updatedPhotos };
    });
  };

  const handleFormSubmit = useCallback(
  async (e) => {
    e.preventDefault();

    if (!selectedUser) {
      alert("⚠️ Please scan a stall QR first before submitting the inspection.");
      return;
    }

    // Build inspection data according to updated questions
    const submission = {
      shopId: selectedUser?.shop_id || "UNKNOWN_SHOP",
      shopName: selectedUser?.shop_name || "Unnamed Stall",
      inspectorId: currentUser?.id || "VOL-UNKNOWN",
      inspectionDate: new Date().toISOString(),
      inspection: {
        // 1️⃣ Is there any use of polythene bags or single-use plastic?
        useOfPlastic: formData.useOfPlastic,

        // 2️⃣ Are biodegradable or paper-based materials used?
        biodegradableMaterialsUsed: formData.biodegradableMaterialsUsed,

        // 3️⃣ Are two dustbins placed at the stall?
        dustbinsAvailable: formData.dustbinsAvailable,

        // 4️⃣ Is waste disposed of at designated points?
        wasteDisposedProperly: formData.wasteDisposedProperly,

        // 5️⃣ Is stall clean? (1–5 scale)
        cleanlinessRating: formData.cleanlinessRating,

        // 6️⃣ Any waste or food on the ground?
        wasteOnGround: formData.wasteOnGround,
        isfoodBusiness:formData.isfoodBusiness,
      },
      remarks: formData.remarks || "",
      photos:
        formData.photos?.map((file) =>
          typeof file === "string" ? file : file.name
        ) || [],
    };

    console.log("🧾 Prepared submission:", submission);
const totalScore = calculateInspectionScore(formData);

try {
  const response = await submitUserForm({
    formData: submission,        // or whatever full obj you want to send as form_details
    userId: selectedUser?.id,    // ensure correct id
    score: totalScore,
  });
  console.log("Submit response:", response);
  alert(`✅ Inspection form submitted successfully! Score: ${totalScore}`);
} catch (err) {
  console.error("Submit failed:", err);
}

    // 🔄 Reset form for next scan
    setFormData({
      isfoodBusiness: "",
      useOfPlastic: "",
      biodegradableMaterialsUsed: "",
      dustbinsAvailable: "",
      wasteDisposedProperly: "",
      cleanlinessRating: "",
      wasteOnGround: "",
      remarks: "",
      photos: [],
    });
    setSelectedUser(null);
  },
  [formData, selectedUser, currentUser]
);

const isFormComplete =
formData.isfoodBusiness &&
  formData.useOfPlastic &&
  formData.biodegradableMaterialsUsed &&
  formData.dustbinsAvailable &&
  formData.wasteDisposedProperly &&
  formData.cleanlinessRating &&
  formData.wasteOnGround;


  // ✅ UI
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4 py-10">
      {/* Banner */}
       <picture>
  {/* Show web-banner.png on screens ≥ 768px */}
  <source
    srcSet="/assets/web-banner.png"
    media="(min-width: 768px)"
  />
  {/* Default (mobile) image */}
  <img
    src="/assets/mobile-banner.png"
    alt="Balijatra Banner"
    className="w-full max-w-sm md:max-w-3xl mb-8 rounded-lg shadow-lg"
  />
</picture>

      <h1 className="text-3xl font-bold mb-6 text-gray-800">
        Volunteer QR Scanner
      </h1>

      {/* Step 1: Start Scan */}
      {!isScanning && !selectedUser && (
        <button
          onClick={handleStartScan}
          className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg shadow-md transition"
        >
          Start QR Scan
        </button>
      )}

      {/* Step 2: Scanner */}
      {isScanning && (
        <div className="flex flex-col items-center space-y-4">
          <div
            id="qr-reader"
            ref={qrRef}
            className="w-80 h-80 bg-gray-200 rounded-lg shadow-md"
          ></div>
          <p className="text-gray-600 text-sm">Point camera at QR code</p>
        </div>
      )}

      {/* Step 3: Error */}
      {error && (
        <div className="mt-4 text-red-600 font-medium text-center">{error}</div>
      )}

      {/* ✅ Step 4: Show Form After Scan */}
     {selectedUser && !isScanning && (
  <div className="w-full max-w-md bg-white p-6 rounded-xl shadow-md mt-6">
    <div className="flex items-center space-x-4 mb-4">
      <img
        src={selectedUser.photo_url}
        alt="Owner"
        className="w-16 h-16 rounded-full object-cover"
      />
      <div>
        <p className="text-lg font-bold">{selectedUser.shop_name}</p>
        <p className="text-sm text-gray-600">
          Owner: {selectedUser.owner_name}
        </p>
        <p className="text-sm text-gray-600">
          Phone: {selectedUser.phone_number}
        </p>
      </div>
    </div>

    <hr className="my-3" />

    {/* 🧾 Inspection Form */}
    <form onSubmit={handleFormSubmit} className="space-y-4">

      {/* 🥗 Q0 - Food Business */}
      <div>
        <label className="block font-medium text-gray-700">
          Is this a food business?
        </label>
        <div className="flex gap-4 mt-1">
          <label>
            <input
              type="radio"
              name="isfoodBusiness"
              value="yes"
              onChange={handleFormChange}
              checked={formData.isfoodBusiness === "yes"}
            />{" "}
            Yes
          </label>
          <label>
            <input
              type="radio"
              name="isfoodBusiness"
              value="no"
              onChange={handleFormChange}
              checked={formData.isfoodBusiness === "no"}
            />{" "}
            No
          </label>
        </div>
      </div>

      {/* Q1 */}
      <div>
        <label className="block font-medium text-gray-700">
          Is there any use of polythene bags, single-use plastic cups, plates,
          spoons, straws, or thermocol materials at the stall?
        </label>
        <div className="flex gap-4 mt-1">
          <label>
            <input
              type="radio"
              name="useOfPlastic"
              value="yes"
              onChange={handleFormChange}
              checked={formData.useOfPlastic === "yes"}
            />{" "}
            Yes
          </label>
          <label>
            <input
              type="radio"
              name="useOfPlastic"
              value="no"
              onChange={handleFormChange}
              checked={formData.useOfPlastic === "no"}
            />{" "}
            No
          </label>
        </div>
      </div>

      {/* Q2 */}
      <div>
        <label className="block font-medium text-gray-700">
          Are only biodegradable, paper, or areca leaf-based materials used for serving and packaging?
        </label>
        <div className="flex gap-4 mt-1">
          <label>
            <input
              type="radio"
              name="biodegradableMaterialsUsed"
              value="yes"
              onChange={handleFormChange}
              checked={formData.biodegradableMaterialsUsed === "yes"}
            />{" "}
            Yes
          </label>
          <label>
            <input
              type="radio"
              name="biodegradableMaterialsUsed"
              value="no"
              onChange={handleFormChange}
              checked={formData.biodegradableMaterialsUsed === "no"}
            />{" "}
            No
          </label>
        </div>
      </div>

      {/* Q3 */}
      <div>
        <label className="block font-medium text-gray-700">
          Are two separate, marked dustbins (wet/dry) placed at the stall?
        </label>
        <div className="flex gap-4 mt-1">
          <label>
            <input
              type="radio"
              name="dustbinsAvailable"
              value="yes"
              onChange={handleFormChange}
              checked={formData.dustbinsAvailable === "yes"}
            />{" "}
            Yes
          </label>
          <label>
            <input
              type="radio"
              name="dustbinsAvailable"
              value="no"
              onChange={handleFormChange}
              checked={formData.dustbinsAvailable === "no"}
            />{" "}
            No
          </label>
        </div>
      </div>

      {/* Q4 */}
      <div>
        <label className="block font-medium text-gray-700">
          Is all stall waste disposed ONLY at designated collection points?
        </label>
        <div className="flex gap-4 mt-1">
          <label>
            <input
              type="radio"
              name="wasteDisposedProperly"
              value="yes"
              onChange={handleFormChange}
              checked={formData.wasteDisposedProperly === "yes"}
            />{" "}
            Yes
          </label>
          <label>
            <input
              type="radio"
              name="wasteDisposedProperly"
              value="no"
              onChange={handleFormChange}
              checked={formData.wasteDisposedProperly === "no"}
            />{" "}
            No
          </label>
        </div>
      </div>

      {/* Q5 - Cleanliness Star Rating */}
      <div>
        <label className="block font-medium text-gray-700 mb-1">
          Is the entire stall area and surroundings kept clean and litter-free?
        </label>
        <p className="text-sm text-gray-500 mb-2">(Rate 1–10 stars, 1 = Very Dirty, 10 = Very Clean)</p>

        <div className="flex gap-2 text-2xl cursor-pointer">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((star) => (
            <span
              key={star}
              onClick={() =>
                handleFormChange({ target: { name: "cleanlinessRating", value: star } })
              }
              className={`transition-transform duration-150 ${
                star <= Number(formData.cleanlinessRating)
                  ? "text-yellow-400 scale-110"
                  : "text-gray-300 hover:text-yellow-300"
              }`}
            >
              ★
            </span>
          ))}
        </div>
      </div>

      {/* Q6 */}
      <div>
        <label className="block font-medium text-gray-700">
          Is there any waste, leftover packaging, or food on the ground during inspection?
        </label>
        <div className="flex gap-4 mt-1">
          <label>
            <input
              type="radio"
              name="wasteOnGround"
              value="yes"
              onChange={handleFormChange}
              checked={formData.wasteOnGround === "yes"}
            />{" "}
            Yes
          </label>
          <label>
            <input
              type="radio"
              name="wasteOnGround"
              value="no"
              onChange={handleFormChange}
              checked={formData.wasteOnGround === "no"}
            />{" "}
            No
          </label>
        </div>
      </div>

      {/* Remarks */}
      <div>
        <label className="block font-medium text-gray-700">
          Additional Remarks (optional)
        </label>
        <textarea
          name="remarks"
          value={formData.remarks}
          onChange={handleFormChange}
          rows="3"
          className="border rounded-md px-2 py-1 w-full mt-1"
          placeholder="Any comments or observations..."
        />
      </div>

      {/* Photos */}
      <div>
        <label className="block font-medium text-gray-700">
          Upload Photos (2–4)
        </label>
      <div className="flex gap-3 flex-wrap">
  {[0, 1, 2, 3].map((index) => (
    <div key={index}>
      <input
        type="file"
        accept="image/*"
        capture="environment" // Added this attribute to directly open the rear camera
        id={`photo-${index}`}
        className="hidden"
        onChange={(e) => handlePhotoChange(e, index)}
      />
      <label
        htmlFor={`photo-${index}`}
        className="w-20 h-20 flex items-center justify-center border border-dashed border-gray-300 rounded-md cursor-pointer hover:bg-gray-100"
      >
        {formData.photos[index] ? (
          <img
            src={URL.createObjectURL(formData.photos[index])}
            alt={`Captured photo ${index + 1}`}
            className="w-full h-full object-cover rounded-md"
          />
        ) : (
          // Using a camera icon to visually indicate a photo capture action
          <i className="fas fa-camera text-gray-500"></i>
        )}
      </label>
    </div>
  ))}
</div>
      </div>

      <button
        type="submit"
        disabled={!isFormComplete}
        className={`w-full mt-4 py-2 rounded-md text-white ${
          isFormComplete
            ? "bg-blue-600 hover:bg-blue-700"
            : "bg-gray-400 cursor-not-allowed"
        }`}
      >
        Submit Form
      </button>
    </form>
  </div>
)}

    </div>
  );
};

export default VolunteerPanel;